import { Chip, Pagination, paginationItemClasses } from "@mui/material";
import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { m } from "framer-motion";
import { varHover } from "src/components/animate";
import Iconify from "src/components/iconify";
import Scrollbar from "src/components/scrollbar/Scrollbar";
import { PRIMARY } from "src/theme/palette";
import NotificationItem from "./notification-item";
import { NOTIFICATIONS_TABS } from "./notifications-tabs";
import useNotificationsDrawer from "./use-notifications-drawer";
// ----------------------------------------------------------------------

export default function NotificationsDrawer() {
  const {
    currentTab,
    drawer,
    handleChangeTab,
    notifications,
    smUp,
    unreadNotificationsCount,
    pagination,
    isLoadingNotifications,
    isUnReadOnly,
    renderPagination,
    handleMarkAllAsRead,
  } = useNotificationsDrawer();

  const renderHead = (
    <Stack direction="row" alignItems="center" sx={{ py: 2, pl: 2.5, pr: 1, minHeight: 68 }}>
      <Typography variant="h6" sx={{ flexGrow: 1 }}>
        Notifications
      </Typography>

      {!!unreadNotificationsCount?.unread_count && (
        <Tooltip title="Mark all as read">
          <IconButton color="primary" onClick={handleMarkAllAsRead}>
            <Iconify icon="eva:done-all-fill" />
          </IconButton>
        </Tooltip>
      )}

      {!smUp && (
        <IconButton onClick={drawer.onFalse}>
          <Iconify icon="mingcute:close-line" />
        </IconButton>
      )}
    </Stack>
  );

  const renderTabs = (
    <Tabs value={currentTab} onChange={handleChangeTab}>
      {NOTIFICATIONS_TABS(
        +notifications?.metadata.total_notifications!,
        unreadNotificationsCount?.unread_count!,
        isUnReadOnly
      ).map((tab) => (
        <Tab
          key={tab.value}
          iconPosition="end"
          value={tab.value}
          label={tab.label}
          icon={
            !isLoadingNotifications ? (
              <Chip
                variant={((tab.value === "all" || tab.value === currentTab) && "filled") || "soft"}
                color={(tab.value === "unread" && "info") || "default"}
                label={tab.count}
              />
            ) : (
              <></>
            )
          }
          sx={{
            "&:not(:last-of-type)": {
              mr: 3,
            },
          }}
        />
      ))}
    </Tabs>
  );

  const renderList = (
    <Scrollbar>
      <List disablePadding>
        {notifications!?.notifications.map((notification) => (
          <NotificationItem
            key={notification.id}
            notification={notification}
            onClick={drawer.onFalse}
          />
        ))}
      </List>
    </Scrollbar>
  );

  return (
    <>
      <IconButton
        component={m.button}
        whileTap="tap"
        whileHover="hover"
        variants={varHover(1.05)}
        color={drawer.value ? "primary" : "default"}
        onClick={drawer.onTrue}
      >
        <Badge badgeContent={unreadNotificationsCount?.unread_count} color="error">
          <Iconify icon="solar:bell-bing-bold-duotone" width={24} />
        </Badge>
      </IconButton>

      <Drawer
        open={drawer.value}
        onClose={drawer.onFalse}
        anchor="right"
        slotProps={{
          backdrop: { invisible: true },
        }}
        PaperProps={{
          sx: { width: 1, maxWidth: 420 },
        }}
      >
        {renderHead}

        <Divider />

        <Stack sx={{ pl: 2.5, pr: 1 }}>{renderTabs}</Stack>

        <Divider />

        {renderList}

        <Box sx={{ p: 1 }}>
          {notifications?.metadata.total_notifications && renderPagination.value && (
            <Pagination
              count={Math.ceil(
                ((isUnReadOnly.value
                  ? unreadNotificationsCount?.unread_count
                  : +notifications?.metadata.total_notifications) || 0) / pagination.rowsPerPage
              )}
              page={pagination.page}
              onChange={(_, page) => pagination.setPage(page)}
              sx={{
                justifyContent: "center",
                display: "flex",
                // set active color to darker
                [`& .${paginationItemClasses.selected}`]: {
                  backgroundColor: PRIMARY.darker,
                },
              }}
            />
          )}
        </Box>
      </Drawer>
    </>
  );
}
